import type { NavigationGuard } from 'vue-router'
import type { AuthGuardConfig, AuthRouteMeta } from './types'
import type { UserRole } from '@repo/client'
import { useAuthStore } from '../stores/auth.store'

declare global {
  interface Window {
    __PLAYWRIGHT_MOCK_USER__?: { role: string }
  }
}

export function createAuthGuard(config: AuthGuardConfig): NavigationGuard {
  return async function authGuard(to, _from, next): Promise<void> {
    const authStore = useAuthStore()
    // Only allow the E2E bypass in test mode AND when the mock user is set.
    // The MODE check ensures production builds can never be fooled by a browser
    // extension or injected script setting window.__PLAYWRIGHT_MOCK_USER__.
    const isE2EMode = import.meta.env.MODE === 'test' && typeof window !== 'undefined' && window.__PLAYWRIGHT_MOCK_USER__ != null

    // Load the session once per browser lifecycle. A null user is a valid,
    // initialized anonymous state and must not trigger a request per route.
    if (!authStore.sessionInitialized && !authStore.loading) {
      await authStore.fetchSession()
    }

    const meta = to.meta as AuthRouteMeta

    // E2E test bypass: skip auth checks but allow navigation
    if (isE2EMode) {
      const mockUser = window.__PLAYWRIGHT_MOCK_USER__
      if (!mockUser) {
        next()
        return
      }

      // Redirect away from guest routes (like login) when mock user exists
      if (meta.guest) {
        next({ name: config.homeRoute })
        return
      }

      // Check role restrictions with mock user's role
      if (meta.roles && meta.roles.length > 0) {
        if (!meta.roles.includes(mockUser.role as UserRole)) {
          next({ name: config.loginRoute, query: { error: 'unauthorized' } })
          return
        }
      }

      // E2E mode: bypass auth check, allow navigation
      next()
      return
    }

    // Normal mode: handle guest routes
    if (meta.guest && authStore.isAuthenticated) {
      next({ name: config.homeRoute })
      return
    }

    // Normal mode: handle protected routes
    if (meta.auth && !authStore.isAuthenticated) {
      next({
        name: config.loginRoute,
        query: { redirect: to.fullPath },
      })
      return
    }

    // Normal mode: handle role-restricted routes
    if (meta.roles && meta.roles.length > 0 && authStore.isAuthenticated) {
      const userRole = config.getRole?.()
      if (!userRole || !meta.roles.includes(userRole)) {
        if (config.unauthorizedRoute) {
          next({ name: config.unauthorizedRoute })
          return
        }
        await authStore.signOut()
        next({ name: config.loginRoute, query: { error: 'unauthorized' } })
        return
      }
    }

    next()
  }
}
